import z from 'zod';
import { UUIDSchema, UnixTimestampSchema } from '../common/schemas';
import { DBNumericSchema, DBTimestampSchema, PageFractionSchema } from '../common/sql';

/**
 * DB Schemas
 *
 * An occurrence records where a question appears in a source document.
 */

export const DBQuestionOccurrenceSchema = z.object({
  id: UUIDSchema,
  rootQuestionId: UUIDSchema,
  sourceDocumentId: UUIDSchema,
  sequenceInDocument: z.number().int().positive().nullable(),
  originalQuestionId: z.string().min(1).max(64),
  originalQuestionNumber: z.string().min(1).max(64),
  snippetAssetId: UUIDSchema.nullable(),
  documentQuestionId: UUIDSchema.nullable(),
  positionInQuestion: z.number().int().positive().nullable(),
  createdAt: DBTimestampSchema,
});

export const DBOccurrenceRegionSchema = z.object({
  id: UUIDSchema,
  occurrenceId: UUIDSchema,
  questionId: UUIDSchema.nullable(),
  pageNumber: z.number().int().positive(),
  bboxX0: DBNumericSchema,
  bboxY0: DBNumericSchema,
  bboxX1: DBNumericSchema,
  bboxY1: DBNumericSchema,
  position: z.number().int().min(0),
  createdAt: DBTimestampSchema,
});

/** The paper a question, or the question a part belongs to, was found in. */
export const DBQuestionPaperTitleSchema = z.object({
  questionId: UUIDSchema,
  label: z.string().min(1),
});

/**
 * Client Schemas
 */

export const QuestionOccurrenceOriginalQuestionIdSchema = z.string().min(1).max(64).openapi({
  description:
    'The label the source document gives the question, including its parent labels, such as 21 or 1(c)',
  example: '21',
});

export const QuestionOccurrenceOriginalQuestionNumberSchema = z.string().min(1).max(64).openapi({
  description:
    'The question number printed in the document that this question sits under, such as 1 for 1(c)',
  example: '1',
});

export const QuestionOccurrenceSequenceInDocumentSchema = z
  .number()
  .int()
  .positive()
  .nullable()
  .openapi({
    description:
      'The place of the question across its whole document, from 1. Null where it is numbered only within its printed question.',
    example: 4,
  });

export const QuestionOccurrencePositionInQuestionSchema = z
  .number()
  .int()
  .positive()
  .nullable()
  .openapi({
    description:
      'The place of the question among those split from its printed question, from 1. Null where it names no printed question.',
    example: 1,
  });

export const QuestionOccurrenceSchema = z.object({
  id: UUIDSchema,
  rootQuestionId: UUIDSchema,
  sourceDocumentId: UUIDSchema,
  sequenceInDocument: QuestionOccurrenceSequenceInDocumentSchema,
  originalQuestionId: QuestionOccurrenceOriginalQuestionIdSchema,
  originalQuestionNumber: QuestionOccurrenceOriginalQuestionNumberSchema,
  snippetAssetId: UUIDSchema.nullable(),
  documentQuestionId: UUIDSchema.nullable().openapi({
    description: 'The printed question this question was split from',
  }),
  positionInQuestion: QuestionOccurrencePositionInQuestionSchema,
  createdAt: UnixTimestampSchema,
});

export const OccurrenceRegionSchema = z.object({
  id: UUIDSchema,
  occurrenceId: UUIDSchema,
  questionId: UUIDSchema.nullable(),
  pageNumber: z.number().int().positive(),
  bboxX0: PageFractionSchema,
  bboxY0: PageFractionSchema,
  bboxX1: PageFractionSchema,
  bboxY1: PageFractionSchema,
  position: z.number().int().min(0),
  createdAt: UnixTimestampSchema,
});

export const QuestionPaperTitleSchema = z.object({
  questionId: UUIDSchema.openapi({ description: 'The question or part asked about' }),
  label: z.string().min(1).openapi({
    description:
      'The paper the question was found in, by its year, institution, course and style, else what it calls itself, else its file name',
    example: '2020 NSB Physics School assessment',
  }),
});
